// FILE: src/hooks/useAuth.tsx
// SECTION: Full File
// PURPOSE: Auth context + Supabase session tracking + Capacitor OAuth deep link handling
// OWNER: SalesPitchMasterAI
// LAST UPDATED: 2026-02-13
// MARKER: AUTH_OAUTH_CAPACITOR_DEEPLINK_FULLFILE_2026_02_13_TASK2_BROWSERFLOW_A

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";

// SECTION (IMPORTS-SUPABASE): Shared supabase client + web-safe OAuth helper
// PURPOSE: Ensure web PKCE verifier is written before redirect
// MARKER: USEAUTH_IMPORT_SUPABASE_WEBSAFE_2026_02_13_A
import { supabase, signInWithOAuthWebSafe } from "../integrations/supabase/client";

import { App as CapApp } from "@capacitor/app";
// SECTION (IMPORTS-CAPACITOR-BROWSER-RESTORE-001)
// ROLE: Import Capacitor Browser used by browserFinished/browserPageLoaded listeners + optional close()
// PURPOSE: Fix TS2304 "Cannot find name 'Browser'"
// MARKER: TASK6_RESTORE_BROWSER_IMPORT_2026_02_14_A
import { Browser } from "@capacitor/browser";

import { Capacitor, registerPlugin } from "@capacitor/core";

// SECTION: Native Apple Sign-In plugin registration (module-level, runs once)
// MARKER: APPLE_SIGN_IN_PLUGIN_REGISTER_2026_03_19_A
interface AppleSignInResponse {
  response: {
    identityToken: string;
    user: string;
    email: string;
    givenName: string;
    familyName: string;
    authorizationCode: string;
    nonce: string;
  };
}
interface AppleSignInPluginInterface {
  authorize(): Promise<AppleSignInResponse>;
}
const AppleSignIn = registerPlugin<AppleSignInPluginInterface>("AppleSignInPlugin");

// SECTION (USEAUTH_GOOGLE_OPTS_TYPE_A1)
// PURPOSE: Allow passing redirectTo from pages (canonical www fix)
// MARKER: USEAUTH_GOOGLE_OPTS_TYPE_2026_02_11_A
type GoogleSignInOptions = {
  redirectTo?: string;
};

type AuthContextValue = {
  user: User | null;
  session: Session | null;
  loading: boolean;

  signInWithPassword: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (opts?: GoogleSignInOptions) => Promise<void>;
  // SECTION: Native Apple Sign-In (ASAuthorizationAppleIDProvider)
  // PURPOSE: Use @capacitor-community/apple-sign-in on iOS to avoid external browser
  // MARKER: USEAUTH_SIGN_IN_WITH_APPLE_TYPE_2026_03_19_A
  signInWithApple: () => Promise<void>;

  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;

  getAccessToken: () => Promise<string | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * SECTION A1 — Capacitor detection helper
 * MARKER: USEAUTH_IS_NATIVE_HELPER_2026_02_12_A
 */
function isNativeCapacitor(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // SECTION B1 — Local state
  // MARKER: USEAUTH_STATE_2026_02_13_A
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // SECTION B1.2 — Optional UI lock to prevent spam clicking sign-in
  // MARKER: OAUTH_SIGNIN_BUTTON_LOCK_2026_02_11_A
  const [isSigningIn, setIsSigningIn] = useState(false);

  // SECTION B1.3 — Native OAuth single-flight lock (ref)
  // PURPOSE: Prevent stacking OAuth flows and allow clean retry after callback/failure
  // MARKER: OAUTH_NATIVE_SINGLEFLIGHT_REF_2026_02_12_A
  const nativeOAuthInFlightRef = useRef(false);

  // SECTION B1.4 — Native OAuth unlock timer handle
  // PURPOSE: Clear lock if iOS never delivers callback
  // MARKER: OAUTH_NATIVE_UNLOCK_TIMER_REF_2026_02_12_A
  const nativeOAuthUnlockTimerRef = useRef<number | null>(null);

    // SECTION B1.6 — Native Browser open state (ref)
  // PURPOSE: Avoid Browser.close() when no Browser is open ("No active window to close!")
  // MARKER: OAUTH_NATIVE_BROWSER_OPEN_REF_2026_02_14_A
  const nativeBrowserIsOpenRef = useRef(false);

  // SECTION: B1.5_RELEASE_NATIVE_OAUTH_LOCK
  // PURPOSE: Close Browser + clear timer + release lock
  // MARKER: OAUTH_NATIVE_RELEASE_HELPER_2026_02_13_TASK2_A
    const releaseNativeOAuthLock = async (reason?: string) => {
    // SECTION (B1.5-CLOSE-BROWSER-SAFELY)
    // PURPOSE: Only close if we actually opened the Browser
    // MARKER: OAUTH_NATIVE_CLOSE_BROWSER_SAFELY_2026_02_14_A
    if (nativeBrowserIsOpenRef.current) {
      try {
        await Browser.close();
      } catch {
        // ignore
      }
      nativeBrowserIsOpenRef.current = false;
    }

    nativeOAuthInFlightRef.current = false;

    if (nativeOAuthUnlockTimerRef.current) {
      window.clearTimeout(nativeOAuthUnlockTimerRef.current);
      nativeOAuthUnlockTimerRef.current = null;
    }

    if (reason) {
      // eslint-disable-next-line no-console
      console.warn("[oauth.native] released", { reason });
    }
  };

  // SECTION B2 — Session bootstrap + listeners (web + native)
  // MARKER: USEAUTH_SESSION_BOOTSTRAP_2026_02_13_A
  useEffect(() => {
    console.log("[MARKER] USEAUTH_BUILD_2026_02_13_TASK2_BROWSERFLOW_A");

    let mounted = true;
    let cleanup = () => {
      /* no-op until initialized */
    };

    (async () => {
      // 1) Initial session
      const { data, error } = await supabase.auth.getSession();
      if (!mounted) return;

      if (error) console.error("[auth.getSession]", error);

      setSession(data?.session ?? null);
      setUser(data?.session?.user ?? null);
      setLoading(false);

      // 2) Auth state changes
      const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);
      });

      // 3) Native deep link OAuth callback
      let appUrlOpenHandler: { remove: () => void } | null = null;
      
      if (isNativeCapacitor()) {
        // SECTION: CAPACITOR_OAUTH_HANDLE_URL
        // PURPOSE: Single handler for BOTH getLaunchUrl() (cold start) and appUrlOpen (warm)
        // MARKER: CAPACITOR_OAUTH_HANDLE_URL_2026_02_13_TASK2_A
        const handleOAuthUrl = async (url?: string | null) => {
          try {
            if (!url) return;

            console.log("[oauth] handleOAuthUrl(raw)", url);

            const lower = url.toLowerCase();
            const isOurCallback =
              lower.startsWith("spmai://auth/callback") || lower.startsWith("spmai://auth/callback/");

            if (!isOurCallback) return;

            console.log("[oauth] handleOAuthUrl(callback)", url);

            // PKCE code flow
            if (url.includes("code=")) {
              const parsed = new URL(url);
              const code = parsed.searchParams.get("code");

              console.log("[oauth] code present", { hasCode: !!code, len: code?.length ?? 0 });

              if (!code) {
                console.error("[oauth] Missing code in callback URL");
                await releaseNativeOAuthLock("missing_code");
                return;
              }

              const { data, error } = await supabase.auth.exchangeCodeForSession(code);

              if (error) {
                console.error("[oauth.exchangeCodeForSession]", {
                  name: (error as any)?.name,
                  status: (error as any)?.status,
                  message: (error as any)?.message,
                  code: (error as any)?.code,
                  details: (error as any)?.details,
                  hint: (error as any)?.hint,
                });
                await releaseNativeOAuthLock("exchange_failed");
                return;
              }

              console.log("[oauth.exchangeCodeForSession] ok", {
                hasSession: !!data?.session,
                hasUser: !!data?.user,
              });

              // Force sync (iOS sometimes misses onAuthStateChange)
              try {
                const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
                if (sessionErr) console.error("[auth.getSession after exchange]", sessionErr);

                const s = sessionData?.session ?? data?.session ?? null;
                setSession(s);
                setUser(s?.user ?? data?.user ?? null);
                setLoading(false);

                console.log("[oauth] forced auth state sync", {
                  hasSession: !!s,
                  hasUser: !!(s?.user ?? data?.user),
                });
              } catch (e) {
                console.error("[oauth] forced auth state sync failed", e);
              }

              await releaseNativeOAuthLock("exchange_ok");
              return;
            }

            // Implicit hash flow (legacy)
            const hashIndex = url.indexOf("#");
            if (hashIndex !== -1) {
              const hash = url.slice(hashIndex + 1);
              const params = new URLSearchParams(hash);

              const access_token = params.get("access_token");
              const refresh_token = params.get("refresh_token");

              console.log("[oauth] hash tokens present", {
                hasAccess: !!access_token,
                hasRefresh: !!refresh_token,
              });

              if (access_token && refresh_token) {
                const { error } = await supabase.auth.setSession({ access_token, refresh_token });
                if (!error) {
                  console.log("[oauth.setSession] ok", {
                    accessLen: access_token.length,
                    refreshLen: refresh_token.length,
                  });
                }
                if (error) console.error("[oauth.setSession]", error);
                await releaseNativeOAuthLock("hash_ok");
                return;
              }

              console.error("[oauth] Missing tokens in hash callback");
              await releaseNativeOAuthLock("hash_missing_tokens");
              return;
            }

            console.error("[oauth] Unrecognized callback format");
            await releaseNativeOAuthLock("unknown_callback_format");
          } catch (e) {
            console.error("[oauth.handleOAuthUrl] unexpected", e);
            void releaseNativeOAuthLock("handleOAuthUrl_unexpected_error");
          }
        };

        // SECTION: CAPACITOR_APPURLOPEN_HANDLER
        // PURPOSE: Handle OAuth redirect while app is already running (warm)
        // MARKER: CAPACITOR_APPURLOPEN_HANDLER_2026_02_13_TASK2_A
        appUrlOpenHandler = await CapApp.addListener("appUrlOpen", async ({ url }) => {
          console.log("[oauth] appUrlOpen(raw)", url);
          await handleOAuthUrl(url);
        });
      }

      cleanup = () => {
        mounted = false;
        sub.subscription.unsubscribe();

        if (appUrlOpenHandler) appUrlOpenHandler.remove();
        
      };
    })();

    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      loading,

      // SECTION C1 — Email/password
      // MARKER: USEAUTH_EMAIL_PASSWORD_2026_02_13_A
      signInWithPassword: async (email, password) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      },

      // SECTION (C2): Google OAuth (web-safe PKCE + native single-flight)
      // MARKER: SIGNIN_WITH_GOOGLE_HONOR_OPTS_2026_02_13_B
      signInWithGoogle: async (opts?: GoogleSignInOptions) => {
        // SECTION (C2-START-CLICK)
// PURPOSE: Confirm the tap is reaching signInWithGoogle immediately
// MARKER: OAUTH_NATIVE_CLICK_2026_02_14_C
console.log("[oauth.native] click: signInWithGoogle()", {
  ts: Date.now(),
  isNative: isNativeCapacitor(),
});

        const isNative = isNativeCapacitor();

        // UI lock (both web + native)
        if (isSigningIn) return;
        setIsSigningIn(true);

        // SECTION (C2-UI-FEEDBACK)
// PURPOSE: Disable button fast / show immediate feedback to prevent double-tap
// MARKER: OAUTH_NATIVE_UI_FEEDBACK_2026_02_14_C
console.log("[oauth.native] isSigningIn=true (UI should disable button now)");

        // Native lock (prevents stacking OAuth flows)
        // SECTION (C2-LOCK): Native single-flight lock
        // MARKER: OAUTH_NATIVE_SINGLEFLIGHT_LOCK_2026_02_13_B
        if (isNative) {
          if (nativeOAuthInFlightRef.current) {
            console.warn("[oauth.native] blocked (already in flight)");
            setIsSigningIn(false);
            return;
          }

          nativeOAuthInFlightRef.current = true;

          // Safety unlock: if callback never arrives, allow retry
          if (nativeOAuthUnlockTimerRef.current) {
            window.clearTimeout(nativeOAuthUnlockTimerRef.current);
          }
          nativeOAuthUnlockTimerRef.current = window.setTimeout(() => {
            console.warn("[oauth.native] unlock timer fired (no callback received)");
            void releaseNativeOAuthLock("unlock_timer");
          }, 120_000); // 2 minutes
        }

        try {
          // SECTION (C2-REDIRECT): Redirect target (LOCKED golden values)
          // ROLE: Enforce canonical www on web; deep link on native
          // PURPOSE: Prevent PKCE storage + origin drift
          // MARKER: OAUTH_REDIRECT_TO_LOCKED_GOLDEN_2026_02_13_A
          const defaultRedirectTo = isNative
            ? "spmai://auth/callback"
            : "https://www.salespitchmasterai.com/auth/callback";

          // NOTE: Even if caller passes opts.redirectTo on web, we keep the canonical golden value.
          const redirectTo = defaultRedirectTo;

          // SECTION (C2-WEBSAFE): Web-safe OAuth initiation
          // ROLE: Prevent PKCE timing race by waiting for verifier write before redirect
          // PURPOSE: Fix "PKCE code verifier not found in storage"
          // MARKER: TASK1_WEBSAFE_OAUTH_CALL_2026_02_13_A
          if (!isNative) {
            await signInWithOAuthWebSafe({
              provider: "google",
              redirectTo,
            });
            return;
          }

                    // SECTION (C2-NATIVE-OPEN-BROWSER)
          // ROLE: Use skipBrowserRedirect to get the URL, then open via Capacitor Browser
          // PURPOSE: Prevent WKWebView redirect timing issues and make Browser.close() valid
          // MARKER: OAUTH_NATIVE_SKIP_REDIRECT_OPEN_BROWSER_2026_02_14_A
          // SECTION (C2-NATIVE-DIRECT-OAUTH)
// ROLE: Let Supabase control redirect flow on native
// PURPOSE: Use ASWebAuthenticationSession internally instead of manual Browser.open
// MARKER: OAUTH_NATIVE_DIRECT_FLOW_2026_02_14_TASK6_A

const { error } = await supabase.auth.signInWithOAuth({
  provider: "google",
  options: {
    redirectTo,
    queryParams: {
      access_type: "offline",
      prompt: "consent",
    },
  },
});

if (error) throw error;

// Supabase handles redirect automatically.
// DO NOT manually open Browser.
// DO NOT use skipBrowserRedirect.
return;

        } catch (e) {
          // If native start fails, release the lock so user can retry
          if (isNative) {
            await releaseNativeOAuthLock("start_oauth_failed");
          }
          throw e;
        } finally {
          setIsSigningIn(false);
        }
      },

      // SECTION C2b — Apple Sign-In (native: AppleSignInPlugin Swift; web: Supabase OAuth)
      // MARKER: USEAUTH_SIGN_IN_WITH_APPLE_IMPL_2026_03_19_B
      signInWithApple: async () => {
        const isNative = isNativeCapacitor();
        if (!isNative) {
          // Web: use standard Supabase OAuth redirect with Services ID (not iOS bundle ID)
          // MARKER: APPLE_WEB_SERVICES_ID_FIX_2026_03_19_A
          const { error } = await supabase.auth.signInWithOAuth({
            provider: "apple",
            options: {
              redirectTo: "https://www.salespitchmasterai.com/auth/callback",
              queryParams: {
                // Force Apple to use the web Services ID, not the iOS bundle ID
                client_id: "com.salespitchmasterai.web",
              },
            },
          });
          if (error) throw error;
          return;
        }
        // Native iOS: use AppleSignInPlugin (Swift, built into App target)
        // This invokes ASAuthorizationAppleIDProvider — the native in-app sheet.
        // No external browser is opened. Apple requires this path.
        try {
          const result = await AppleSignIn.authorize();
          const idToken = result?.response?.identityToken;
          if (!idToken) throw new Error("Apple Sign-In: no identity token returned");
          // Exchange Apple ID token with Supabase
          const { error } = await supabase.auth.signInWithIdToken({
            provider: "apple",
            token: idToken,
            nonce: result.response.nonce,
          });
          if (error) throw error;
        } catch (e: any) {
          // User cancelled — not an error
          if (
            e?.message?.includes("cancel") ||
            e?.message?.includes("Cancel") ||
            e?.code === "1001" ||
            e?.errorCode === "1001"
          ) {
            return;
          }
          throw e;
        }
      },

      // SECTION C3 — Sign out
      // MARKER: USEAUTH_SIGNOUT_2026_02_13_A
      signOut: async () => {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
      },

      // SECTION C4 — Reset password
      // MARKER: USEAUTH_RESET_PASSWORD_2026_02_13_A
      resetPassword: async (email) => {
        const redirectTo = "https://www.salespitchmasterai.com/auth/callback";
        const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
        if (error) throw error;
      },

      // SECTION C5 — Token helper
      // MARKER: USEAUTH_GET_ACCESS_TOKEN_2026_02_13_A
      getAccessToken: async () => {
        if (session?.access_token) return session.access_token;

        const { data, error } = await supabase.auth.getSession();
        if (error) console.error("[auth.getSession in getAccessToken]", error);

        return data?.session?.access_token ?? null;
      },
    }),
    [user, session, loading, isSigningIn]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// SECTION D1 — Hook
// MARKER: USEAUTH_HOOK_EXPORT_2026_02_13_A
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
